import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import LegalCenter from "./pages/LegalCenter";
import PublicContent from "./pages/PublicContent";
import Dashboard from "./pages/Dashboard";
import Onboarding from "./pages/Onboarding";
import CrmInsights from "./pages/CrmInsights";
import Billing from "./pages/Billing";
import Team from "./pages/Team";
import Support from "./pages/Support";
import Calia from "./pages/Calia";
import CheckoutStart from "./pages/CheckoutStart";
import ConnectCRM from "./pages/ConnectCRM";
import ScanReport from "./pages/ScanReport";
import CommandCenter from "./pages/admin/CommandCenter";
import SecurityCenter from "./pages/admin/SecurityCenter";
import AuditCenter from "./pages/admin/AuditCenter";
import AccessCenter from "./pages/admin/AccessCenter";
import CrmOperations from "./pages/admin/CrmOperations";
import ConfigurationCenter from "./pages/admin/ConfigurationCenter";
import JobManagement from "./pages/admin/JobManagement";
import BusinessIntelligence from "./pages/admin/BusinessIntelligence";
import CustomerIntelligenceCenter from "./pages/admin/CustomerIntelligenceCenter";
import SupportCenter from "./pages/admin/SupportCenter";
import ComingSoonCenter from "./pages/admin/ComingSoonCenter";
import ErrorBoundary from "./components/ErrorBoundary";
import { LoadingState } from "./components/LoadingState";
import RouteMetadata from "./seo/RouteMetadata";
import "./App.css";

function FullLoader() {
  return (
    <div className="min-h-screen bg-background px-6 py-20">
      <div className="mx-auto max-w-5xl">
        <LoadingState label="Loading CleanIQ" />
      </div>
    </div>
  );
}

function Protected({ children, admin }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  return (
    <>
      <RouteMetadata />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/product" element={<PublicContent />} />
        <Route path="/connectors" element={<PublicContent />} />
        <Route path="/use-cases" element={<PublicContent />} />
        <Route path="/pricing" element={<PublicContent />} />
        <Route path="/faq" element={<PublicContent />} />
        <Route path="/contact" element={<PublicContent />} />
        <Route path="/resources" element={<PublicContent />} />
        <Route path="/trust" element={<LegalCenter />} />
        <Route path="/terms" element={<LegalCenter />} />
        <Route path="/privacy" element={<LegalCenter />} />
        <Route path="/refunds" element={<LegalCenter />} />
        <Route path="/security-overview" element={<LegalCenter />} />
        <Route path="/subprocessors" element={<LegalCenter />} />
        <Route path="/acceptable-use" element={<LegalCenter />} />
        <Route path="/cookies" element={<LegalCenter />} />
        <Route path="/ai-transparency" element={<LegalCenter />} />
        <Route path="/availability" element={<LegalCenter />} />
        <Route path="/copyright" element={<LegalCenter />} />
        <Route path="/dpa" element={<LegalCenter />} />
        <Route path="/dashboard" element={<ErrorBoundary><Protected><Dashboard /></Protected></ErrorBoundary>} />
        <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
        <Route path="/insights" element={<Protected><CrmInsights /></Protected>} />
        <Route path="/billing" element={<Protected><Billing /></Protected>} />
        <Route path="/team" element={<Protected><Team /></Protected>} />
        <Route path="/support" element={<Protected><Support /></Protected>} />
        <Route path="/calia" element={<ErrorBoundary><Protected><Calia /></Protected></ErrorBoundary>} />
        <Route path="/checkout/:plan" element={<Protected><CheckoutStart /></Protected>} />
        <Route path="/connect" element={<Protected><ConnectCRM /></Protected>} />
        <Route path="/scans/:id" element={<ErrorBoundary><Protected><ScanReport /></Protected></ErrorBoundary>} />
        <Route path="/admin" element={<Navigate to="/admin/command-center" replace />} />
        <Route path="/admin/command-center" element={<Protected admin><CommandCenter /></Protected>} />
        <Route path="/admin/security" element={<Protected admin><SecurityCenter /></Protected>} />
        <Route path="/admin/audit" element={<Protected admin><AuditCenter /></Protected>} />
        <Route path="/admin/crm-operations" element={<Protected admin><CrmOperations /></Protected>} />
        <Route path="/admin/access" element={<Protected admin><AccessCenter /></Protected>} />
        <Route path="/admin/configuration" element={<Protected admin><ConfigurationCenter /></Protected>} />
        <Route path="/admin/jobs" element={<Protected admin><JobManagement /></Protected>} />
        <Route path="/admin/analytics" element={<Protected admin><BusinessIntelligence /></Protected>} />
        <Route path="/admin/intelligence" element={<Protected admin><CustomerIntelligenceCenter /></Protected>} />
        <Route path="/admin/support" element={<Protected admin><SupportCenter /></Protected>} />
        <Route path="/admin/center/:key" element={<Protected admin><ComingSoonCenter /></Protected>} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster richColors position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}
